// third-party
import * as yup from 'yup';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// project import
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeContactTypeForm } from 'store/reducers/contact-type';
import ContactTypeDto from 'common/dto-types/contacts/contact-type-dto';
import { createContactType, updateContactType } from './api';
import { openSnackbar } from 'store/reducers/snackbar';

type ContactTypeFromProps = {
    refreshCallback: () => void;
};

const ContactTypeForm = ({ refreshCallback }: ContactTypeFromProps): JSX.Element => {
    const data = useSelector((state) => state.contactType.data);
    const isCreating = !data;

    const initialValues: ContactTypeDto = {
        id: data?.id ?? 0,
        name: data?.name ?? '',
    };

    const validationSchema = yup.object({
        name: yup.string().required('Name is required').max(255, 'Name cannot exceed 255 characters'),
    });

    const handleFormSubmit = async (values: ContactTypeDto, { setSubmitting }: FormikHelpers<ContactTypeDto>) => {
        setSubmitting(true);
        let data;
        let confirmMessage;
        let failedMessage;

        if (isCreating) {
            data = await createContactType(values);
            confirmMessage = 'Contact Type created successfully!';
            failedMessage = 'Failed to create Contact Type';
        } else {
            data = await updateContactType(values);
            confirmMessage = 'Contact Type updated successfully!';
            failedMessage = 'Failed to update Contact Type';
        }

        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: confirmMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            dispatch(closeContactTypeForm());
            refreshCallback();
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
            setSubmitting(false);
        }
    };

    const formik = useFormik<ContactTypeDto>({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit,
    });

    const { errors, touched, dirty, isValid, getFieldProps, handleSubmit, isSubmitting } = formik;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} ContactType</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <InputLabel>
                                ContactType Name <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="name"
                                placeholder="Enter Contact Type Name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                {...getFieldProps('name')}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" onClick={() => dispatch(closeContactTypeForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isSubmitting}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default ContactTypeForm;
